import { action, makeObservable, observable } from "mobx";
import { ICondition } from "./Condition";
import { ConditionExpressionType } from "./ConditionExpressionType";
import { v4 } from "uuid";
import { LLMConstants } from "models/LLMs/constants";
import { isModelVariable } from "models/LLMs/LLMVariable";

export interface IConditionPrimitive extends ICondition {
    propertyName: string;
    operation: ConditionExpressionType;
    value: string;
    propertyType?: ConditionPropertyType;
    isCaseSensitive?: boolean;
    classifierId?: string;
}

export enum ConditionPropertyType {
    /**
     * Indicates that the propertyName is a step variable
     */
    Step,
    /**
     * Indicates that the propertyName is an amd value variable
     */
    Conversation,
}

export class ConditionPrimitive implements IConditionPrimitive {
    public readonly id: string = v4();

    /**
     * The full property name in the form of `{stepId}.{shortPropertyName}`
     */
    @observable propertyName: string;
    /**
     * The name we show to the user
     */
    @observable shortPropertyName: string;
    @observable operation: ConditionExpressionType;
    @observable value: string;
    @observable isCaseSensitive: boolean;
    @observable classifierId?: string;

    @observable propertyType: ConditionPropertyType;

    constructor();
    constructor(conditionPrimitive: IConditionPrimitive);
    constructor(conditionPrimitive?: IConditionPrimitive) {
        makeObservable(this);

        if (conditionPrimitive) {
            Object.assign(this, conditionPrimitive);
            // Older V2 drafts may contain this now-derived display value. Do
            // not carry it forward when the condition is saved again.
            delete (this as unknown as Record<string, unknown>)
                .classifierName;
            delete (this as unknown as Record<string, unknown>)
                .ClassifierName;
            // A model output's display label can differ from the trailing part
            // of its qualified property name, so preserve its saved short name.
            // Everything else still derives one from PropertyName.
            const keepSavedShortName =
                isModelVariable(conditionPrimitive.propertyName) &&
                !!this.shortPropertyName;
            if (!keepSavedShortName) {
                this.shortPropertyName =
                    conditionPrimitive.propertyName.split(".").pop() ?? "";
            }
        } else {
            this.propertyName = "";
            this.operation = ConditionExpressionType.Equal;
            this.value = "";
            this.isCaseSensitive = false;
        }

        if (!this.propertyType) this.propertyType = ConditionPropertyType.Step;
    }

    private determinePropertyType(propertyName: string): ConditionPropertyType {
        if (propertyName.startsWith(LLMConstants.AmdValueVariablePrefix)) {
            return ConditionPropertyType.Conversation;
        }

        return ConditionPropertyType.Step;
    }

    /**
     * Sets the property type and clears the property name and short property name.
     * @param propertyType
     */
    @action
    public setPropertyType(propertyType: ConditionPropertyType) {
        this.propertyType = propertyType;
        this.propertyName = "";
        this.shortPropertyName = "";
    }

    @action
    public setPropertyName(propertyName: string) {
        this.propertyName = propertyName;
        this.propertyType = this.determinePropertyType(propertyName);
    }

    @action
    public setShortPropertyName(propertyName: string) {
        this.shortPropertyName = propertyName;
    }

    @action
    public setOperation(operation: ConditionExpressionType) {
        this.operation = operation;
    }

    @action
    public setValue(value: string) {
        this.value = value;
    }
    
    @action
    public setIsCaseSensitive(isCaseSensitive: boolean) {
        this.isCaseSensitive = isCaseSensitive;
    }

    @action
    public setClassifierId(classifierId?: string) {
        this.classifierId = classifierId;
    }

    public static isConditionPrimitive(
        condition: ICondition,
    ): condition is IConditionPrimitive {
        return (
            Object.hasOwn(condition, "propertyName") &&
            Object.hasOwn(condition, "operation") &&
            Object.hasOwn(condition, "value")
        );
    }
}
